import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/constants";
import { ClipboardList, PackageSearch, CheckCircle2, Wrench, Truck, Receipt, Wallet, Archive, AlertTriangle, ArrowRight } from "lucide-react";

const CARDS = [
  { key: "waiting_parts", label: "Waiting Parts", icon: PackageSearch },
  { key: "parts_ready_booking", label: "Parts Complete / Booking", icon: CheckCircle2 },
  { key: "ready_to_delivery", label: "Ready to Delivery", icon: Truck },
  { key: "pending_billing", label: "Pending Billing", icon: Receipt },
  { key: "outstanding_receivables", label: "Outstanding Receivables", icon: Wallet, money: true },
  { key: "closed_this_month", label: "Closed This Month", icon: Archive },
];

const PHASES = [
  { key: "PRE_ORDER", label: "Pre Order", to: "/pre-order", icon: ClipboardList, tone: "border-blue-200" },
  { key: "REPAIR_ORDER", label: "Repair Order", to: "/repair-order", icon: Wrench, tone: "border-amber-200" },
  { key: "FINISH_ORDER", label: "Finish Order", to: "/finish-order", icon: Truck, tone: "border-violet-200" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/dashboard/stats").then((r) => setData(r.data)); }, []);
  const stats = data?.stats || {};
  const phases = data?.phases || {};
  const alerts = data?.alerts || [];

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-[#111827] sm:text-3xl">Dashboard</h1>
      <p className="mt-1 text-sm text-gray-500">One master claim moving through three phases</p>

      {/* Three operational phases */}
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        {PHASES.map((p) => {
          const d = phases[p.key] || {};
          return (
            <button key={p.key} data-testid={`phase-card-${p.key}`} onClick={() => navigate(p.to)} className={`rounded-xl border-2 bg-white p-5 text-left shadow-sm transition-transform hover:-translate-y-0.5 ${p.tone}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading text-sm font-bold uppercase tracking-wider text-[#111827]">{p.label}</span>
                <p.icon size={20} className="text-[#3B82F6]" />
              </div>
              <div className="mt-3 font-heading text-3xl font-bold text-[#111827]">{d.count ?? 0}</div>
              <div className="mt-2 space-y-0.5 text-xs text-gray-500">
                {p.key !== "FINISH_ORDER" && <div>SPK value: <b className="text-gray-700">{formatRupiah(d.spk_value)}</b></div>}
                {p.key === "REPAIR_ORDER" && <div>Avg aging: <b className="text-gray-700">{d.avg_aging ?? 0} day(s)</b></div>}
                {p.key === "FINISH_ORDER" && <div>Invoice value: <b className="text-gray-700">{formatRupiah(d.invoice_value)}</b></div>}
                {p.key === "FINISH_ORDER" && <div>Outstanding: <b className="text-gray-700">{formatRupiah(d.outstanding)}</b></div>}
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#8a6d1a]">Open queue <ArrowRight size={13} /></div>
            </button>
          );
        })}
      </div>

      {/* Operational snapshot */}
      <h2 className="mt-8 font-heading text-lg font-semibold text-[#111827]">Snapshot</h2>
      <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {CARDS.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{c.label}</span>
              <c.icon size={16} className="text-[#3B82F6]" />
            </div>
            <div className="mt-2 font-heading text-lg font-bold text-[#111827]">{c.money ? formatRupiah(stats[c.key]) : (stats[c.key] ?? 0)}</div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <h2 className="font-heading text-lg font-semibold text-[#111827]">Alerts</h2>
        <div className="mt-3 space-y-2">
          {alerts.length === 0 && <p className="text-sm text-gray-400">No alerts. Everything on track.</p>}
          {alerts.map((a, i) => (
            <button key={i} data-testid={`alert-${i}`} onClick={() => navigate(`/claims/${a.claim_id}`)} className="flex w-full items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100">
              <AlertTriangle size={18} className="shrink-0 text-amber-600" />
              <span className="license-plate text-xs">{a.license_plate}</span>
              <span className="text-sm text-amber-800">{a.message}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
